import { Injectable } from '@angular/core';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class ContactHelperService {
  getFirstName(fullName: string): string {
    const names = fullName.trim().split(' ');
    return names[0] || '';
  }

  getLastName(fullName: string): string {
    const names = fullName.trim().split(' ');
    return names[1] || '';
  }

  findWorkEmail(locators: any[] = []): string {
    const workEmail = locators.find((locator: any) =>
      locator.typeCode === 'email' && locator.email?.purpose === 'work'
    );

    return workEmail?.email?.address ?? '';
  }

  findWorkPhone(locators: any[] = []): { number: string; extension: string } {
    const workPhone = locators.find((locator: any) =>
      locator.typeCode === 'phone' && locator.phone?.purpose === 'work'
    );

    return {
      number: workPhone?.phone?.number ?? '',
      extension: workPhone?.phone?.extension ?? ''
    };
  }

  findNameTextByFunction(names: any[] = [], functionName: string): string {
    const nameEntry = names.find((name: any) => name.function === functionName);
    return nameEntry?.text ?? '';
  }

  sortByPathInPlace(items: any[], path: string): void {
    if (!Array.isArray(items) || !path) {
      return;
    }

    items.sort((a, b) => {
      const aValue = _.toLower(_.toString(_.get(a, path, '')));
      const bValue = _.toLower(_.toString(_.get(b, path, '')));

      if (aValue < bValue) {
        return -1;
      }

      if (aValue > bValue) {
        return 1;
      }

      return 0;
    });
  }

  mapToNameIdOptions(items: any[]): Array<{ name: string; id: string }> {
    return (items || []).map((item: any) => ({
      name: item.party.displayName,
      id: item.party.id
    }));
  }

  findPhoneByPurpose(locators: any[] = [], purpose: string): { number: string; extension: string } {
    const phoneLocator = locators.find((locator: any) =>
      locator.typeCode === 'phone' && locator.phone?.purpose === purpose
    );

    return {
      number: phoneLocator?.phone?.number ?? '',
      extension: phoneLocator?.phone?.extension ?? ''
    };
  }

  findEmailAddress(locators: any[] = []): string {
    const emailLocator = locators.find((locator: any) => locator.typeCode === 'email');
    return emailLocator?.email?.address ?? '';
  }

  findLocatorKeys(locators: any[] = [], typeCode: string): any[] {
    const locator = locators.find((item: any) => item.typeCode === typeCode && item.partyLocatorKeys);
    return locator?.partyLocatorKeys ?? [];
  }

  formatPhoneDisplay(number: string, extension: string): string {
    return extension ? `${number} extn. ${extension}` : number ? number : '--';
  }

  extractContactCore(contactItem: any): {
    displayName: string;
    email: string;
    workPhone: { number: string; extension: string };
    titleCode: string;
    barNumber: string;
    phonePartyLocatorKeys: any[];
    emailPartyLocatorKeys: any[];
    isActive: boolean;
  } {
    const names = contactItem?.party?.names ?? [];
    const locators = contactItem?.party?.locators ?? [];

    return {
      displayName: contactItem?.party?.displayName ?? '',
      email: this.findEmailAddress(locators),
      workPhone: this.findPhoneByPurpose(locators, 'work'),
      titleCode: this.findNameTextByFunction(names, 'title'),
      barNumber: this.findNameTextByFunction(names, 'barNumber'),
      phonePartyLocatorKeys: this.findLocatorKeys(locators, 'phone'),
      emailPartyLocatorKeys: this.findLocatorKeys(locators, 'email'),
      isActive: contactItem?.active === true
    };
  }

  buildContactPartyPayload(payload: {
    displayName: string;
    titleCode: string;
    barNumber: string;
    phone: string;
    extension: string;
    email: string;
    phonePartyLocatorKeys: any[];
    emailPartyLocatorKeys: any[];
    active: boolean;
  }): any {
    return {
      party: {
        displayName: payload.displayName,
        typeCode: 'Person',
        typeCodeCustomerValue: 'string',
        interpreterNeeded: true,
        interpretedLanguage: 'string',
        names: [
          {
            function: 'first-name',
            text: this.getFirstName(payload.displayName)
          },
          {
            function: 'last-name',
            text: this.getLastName(payload.displayName)
          },
          {
            function: 'title',
            text: payload.titleCode
          },
          {
            function: 'barNumber',
            text: payload.barNumber
          }
        ],
        locators: [
          {
            typeCode: 'phone',
            phone: {
              purpose: 'work',
              number: payload.phone,
              extension: payload.extension
            },
            partyLocatorKeys: payload.phonePartyLocatorKeys
          },
          {
            typeCode: 'email',
            email: {
              purpose: 'work',
              address: payload.email
            },
            partyLocatorKeys: payload.emailPartyLocatorKeys
          }
        ]
      },
      active: payload.active,
      tags: [
        {
          tagName: payload.titleCode
        }
      ]
    };
  }
}
